import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function FormShell({
  backTo,
  backLabel,
  eyebrow,
  title,
  belowTitle,
  rightHeader,
  children,
  footer,
}) {
  return (
    <div>
      <div className="mb-4">
        <Link
          to={backTo}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-leechiuBlue"
        >
          <ArrowLeft className="w-4 h-4" />
          {backLabel}
        </Link>
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          {eyebrow ? (
            <div className="cms-eyebrow mb-1.5">{eyebrow}</div>
          ) : null}
          <h1>{title}</h1>
          {belowTitle ? <div className="mt-2">{belowTitle}</div> : null}
        </div>
        {rightHeader ? (
          <div className="flex items-center gap-2">{rightHeader}</div>
        ) : null}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">{children}</div>

      <div className="sticky bottom-0 mt-8 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 bg-white/90 backdrop-blur border-t border-gray-200 flex items-center justify-end gap-2">
        {footer}
      </div>
    </div>
  );
}

export function FormSection({ title, description, children, className = "" }) {
  return (
    <section
      className={`bg-white rounded-2xl ring-1 ring-gray-100 shadow-sm p-5 ${className}`}
    >
      {title ? (
        <header className="mb-4">
          <h3>{title}</h3>
          {description ? (
            <p className="mt-1 text-xs text-gray-500">{description}</p>
          ) : null}
        </header>
      ) : null}
      <div className="space-y-4">{children}</div>
    </section>
  );
}

export function TextField({
  label,
  required,
  hint,
  error,
  className = "",
  ...rest
}) {
  return (
    <label className={`block ${className}`}>
      {label ? (
        <span className="cms-eyebrow block mb-1.5 text-gray-600">
          {label}
          {required ? <span className="text-leechiuOrange ml-0.5">*</span> : null}
        </span>
      ) : null}
      <input
        {...rest}
        className={`w-full px-3 py-2 rounded-lg border ${
          error ? "border-red-300" : "border-gray-200"
        } bg-white focus:ring-2 focus:ring-leechiuOrange focus:border-transparent outline-none text-sm`}
      />
      {error ? (
        <span className="block text-xs text-red-600 mt-1">{error}</span>
      ) : hint ? (
        <span className="block text-xs text-gray-400 mt-1">{hint}</span>
      ) : null}
    </label>
  );
}

export function TextArea({ label, required, hint, rows = 3, ...rest }) {
  return (
    <label className="block">
      {label ? (
        <span className="cms-eyebrow block mb-1.5 text-gray-600">
          {label}
          {required ? <span className="text-leechiuOrange ml-0.5">*</span> : null}
        </span>
      ) : null}
      <textarea
        rows={rows}
        {...rest}
        className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white focus:ring-2 focus:ring-leechiuOrange focus:border-transparent outline-none text-sm"
      />
      {hint ? (
        <span className="block text-xs text-gray-400 mt-1">{hint}</span>
      ) : null}
    </label>
  );
}

export function PublishToggle({ value, onChange }) {
  return (
    <div className="flex items-center justify-between p-3 rounded-xl bg-gray-50 ring-1 ring-gray-100">
      <div>
        <div className="text-sm font-medium text-leechiuBlue">Status</div>
        <div className="text-xs text-gray-500">
          {value ? "Visible on the public website" : "Hidden — saved as draft"}
        </div>
      </div>
      <button
        type="button"
        onClick={() => onChange(!value)}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors ${
          value ? "bg-emerald-500" : "bg-gray-300"
        }`}
        role="switch"
        aria-checked={value}
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition mt-0.5 ${
            value ? "translate-x-5" : "translate-x-0.5"
          }`}
        />
      </button>
    </div>
  );
}
